import { bavLimsAnmelden } from '@/api/axios';
import AppLayout from '@/layout/AppLayout.vue';
import { useNavigationStore } from '@/stores/navigationStore';
import { istBenutzerAngemeldet } from '@/utils/auth';
import { getFehlerRoute } from '@/utils/globalErrorHandlers';
import { createRouter, createWebHistory } from 'vue-router';
import {
    createAuthRouten,
    createBegehungsberichteRouten,
    createDashboardRoute,
    createErgebnisRouten,
    createEtikettenRouten,
    createFehlerRouten,
    createGutachtenRouten,
    createHilfeRouten,
    createKundenbereichRouten,
    createMustervorlagenRouten,
    createPagesRouten,
    createProbenRouten,
    createWissensdatenbankRoutes,
    createZertifikateRoutes,
    RouterHelpers
} from './routeHelpers';
import { ROUTE_NAMES } from './routeNames';

const router = createRouter({
    // Base URL wird von Vite zur Build-Zeit bereitgestellt (aus VITE_APP_BASE_URL)
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/:kunde?',
            component: AppLayout,
            meta: { requiresAuth: true }, // Protect the entire layout
            props: (route) => ({
                kunde: route.params.kunde || null
            }),
            children: [
                createDashboardRoute(),
                ...createProbenRouten(),
                ...createMustervorlagenRouten(),
                ...createErgebnisRouten(),
                ...createBegehungsberichteRouten(),
                ...createGutachtenRouten(),
                ...createEtikettenRouten(),
                ...createPagesRouten(),
                ...createZertifikateRoutes(),
                ...createWissensdatenbankRoutes(),
                ...createKundenbereichRouten(),
                ...createFehlerRouten(true),
                ...createHilfeRouten(),
                {
                    path: '/:kunde?/nutzungsbedingungen',
                    name: ROUTE_NAMES.KUNDENBEREICH.NUTZUNGSBEDINGUNGEN,
                    component: () => import('@/views/pages/Nutzungsbedingungen.vue'),
                    props: (route) => ({ formularAnzeigen: false }),
                    meta: { requiresAuth: true, ausstehendeAenderungenPruefungUeberspringen: true }
                }
            ]
        },
        {
            path: '/pages/notfound',
            name: ROUTE_NAMES.PAGES.NOT_FOUND,
            component: () => import('@/views/pages/fehler/NotFound.vue'),
            meta: { requiresAuth: false, ausstehendeAenderungenPruefungUeberspringen: true } // Public route
        },
        ...createAuthRouten(),
        {
            path: '/konto/bavlimsanmelden',
            name: ROUTE_NAMES.AUTH.BAV_LIMS_ANMELDEN,
            beforeEnter: async (to) => {
                await bavLimsAnmelden(to);
            },
            meta: { requiresAuth: false }
        },
        {
            path: '/:kunde?/vorteile-von-mybav',
            name: ROUTE_NAMES.VORTEILE_VON_MYBAV,
            component: () => import('@/views/pages/VorteileVonMyBAV.vue'),
            meta: { requiresAuth: false, ausstehendeAenderungenPruefungUeberspringen: true }
        },
        ...createFehlerRouten(false),
        {
            path: '/:pathMatch(.*)*',
            name: ROUTE_NAMES.NOT_FOUND,
            component: () => import('@/views/pages/fehler/NotFound.vue'),
            meta: { requiresAuth: false, ausstehendeAenderungenPruefungUeberspringen: true } // Public route
        }
    ]
});

// Navigation guard to protect routes based on meta fields
router.beforeEach(async (to, from, next) => {
    const navigationStore = useNavigationStore();
    navigationStore.istNavigationAktiv = true;

    // Siehe navigationStore.ausstehendeNavigationReturnUrl:
    // Wir müssen uns die Ziel-Url merken, damit sie nicht im Fall vom Sliding Timeout verloren geht.
    navigationStore.ausstehendeNavigationReturnUrl = to.fullPath;

    // Check if the route requires authentication
    // Default to true if meta.requiresAuth is not specified
    const authentifizierungBenoetigt = to.matched.some((record) => (record.meta.requiresAuth !== undefined ? record.meta.requiresAuth : true));
    const kundendatenLadenUeberspringen = to.matched.some((record) => record.meta.kundendatenLadenUeberspringen === true);

    if (authentifizierungBenoetigt && !istBenutzerAngemeldet()) {
        // If authentication is required but user is not authenticated, redirect to login
        next({
            name: ROUTE_NAMES.AUTH.LOGIN,
            query: { returnUrl: to.fullPath }
        });
        return;
    }

    // Kundendaten für alle authentifizierten Routen laden,
    // außer bei Routen, die das Laden explizit überspringen (z. B. Fehlerseiten)
    if (authentifizierungBenoetigt && !kundendatenLadenUeberspringen) {
        // Dynamischer Import des Stores um zirkuläre Abhängigkeiten zu vermeiden
        const { useKundenbereichStore } = await import('@/stores/kundenbereichStore');
        const kundenbereichStore = useKundenbereichStore();

        // Lade Kundendaten falls noch nicht geladen
        if (!kundenbereichStore.datenGeladen) {
            try {
                await kundenbereichStore.ladeKundenDaten(to.params.kunde);
            } catch (error) {
                // Sonderfall: Auth-/Session-Fehler (z. B. 401/419), die vom Axios-Interceptor
                // mit _skipGlobalErrorHandling markiert wurden.
                // Diese Fehler werden hier bewusst NICHT lokal auf die Fehlerseite umgeleitet,
                // damit der vorgesehene Auth-Flow (Refresh/Logout/Login-Redirect) greifen kann.
                if (error._skipGlobalErrorHandling) {
                    throw error;
                }

                // Bei Fehlern im Guard direkt auf die Fehlerroute umleiten.
                // So wird die laufende Navigation mit next(...) eindeutig aufgelöst
                // und wir vermeiden einen separaten globalen Error-Flow (next(error) + router.onError).
                const fehlerRoute = getFehlerRoute(router, error);
                next(fehlerRoute);
                return;
            }
        }

        // Prüfe Kundennummer nur wenn ein Kunde-Parameter vorhanden ist
        if (to.params.kunde && !kundenbereichStore.istGueltigeKundennummer(to.params.kunde)) {
            // Umleitung zur 403-Fehlerseite
            // Angabe des kunde-Param problematisch
            next(RouterHelpers.toAccessDenied(istBenutzerAngemeldet()));
            return;
        }
    }

    // Wenn nach der Anmeldung auf die Startseite navigiert wird,
    // soll das Menü standardmäßig aufgeklappt sein, wenn es sich um die Maobilansicht handelt.
    // Dies soll aber nur beim Navigieren von der Anmeldung zur Startseite erfolgen.
    if (from.name === ROUTE_NAMES.AUTH.LOGIN && to.name === ROUTE_NAMES.DASHBOARD) {
        to.meta.NavigationVonAnmeldung = true;
    }

    // Otherwise proceed as normal
    next();
});

router.afterEach((to) => {
    const navigationStore = useNavigationStore();
    navigationStore.istNavigationAktiv = false;

    // ggf. zwischengespeicherte Ziel-Url zurücksetzen (siehe beforeEach)
    navigationStore.ausstehendeNavigationReturnUrl = null;

    if (to.name === ROUTE_NAMES.AUTH.LOGIN) {
        navigationStore.ausstehendeAenderungenPruefungUeberspringen = false;
    }
});

export default router;
